import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { Kicker, Reveal } from "../components/Reveal";
import { api, apiError } from "../lib/api";

const input = "mt-1.5 w-full rounded-md border border-linen bg-ivory px-3 py-2.5 text-sm outline-none focus:border-forest";

function RequestModal({ plan, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/membership-requests", { plan_id: plan.id, ...form });
      toast.success("Request received! Nichole will confirm and collect payment at the salon.");
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/50 p-4" onClick={onClose}>
      <form data-testid="membership-request-modal" onSubmit={submit} className="w-full max-w-md rounded-lg border border-linen bg-ivory p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-serif text-2xl font-medium text-forest">Request {plan.name}</h3>
            <p className="mt-1 text-sm text-moss">${plan.price}/{plan.interval} &middot; pay at the salon, no card needed now</p>
          </div>
          <button type="button" data-testid="membership-request-close" onClick={onClose} className="text-moss hover:text-forest"><X size={18} /></button>
        </div>
        <div className="mt-6 space-y-4">
          <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Full name</label><input data-testid="membership-name-input" required value={form.name} onChange={set("name")} className={input} /></div>
          <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Email</label><input data-testid="membership-email-input" type="email" required value={form.email} onChange={set("email")} className={input} /><p className="mt-1 text-xs text-moss">Use your patient portal email so the membership shows up in your account.</p></div>
          <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Phone</label><input data-testid="membership-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={input} /></div>
          <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-moss">Anything Nichole should know? (optional)</label><textarea data-testid="membership-note-input" rows={2} value={form.note} onChange={set("note")} className={input} /></div>
        </div>
        <button type="submit" data-testid="membership-request-submit" disabled={busy} className="mt-6 w-full rounded-md bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50">
          {busy ? "Sending..." : "Send Request"}
        </button>
      </form>
    </div>
  );
}

export default function MembershipsSection() {
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/membership-plans").then((res) => setPlans(res.data)).catch(() => setPlans([]));
  }, []);

  if (plans.length === 0) return null;
  const placeholder = plans.some((p) => p.is_placeholder);
  const ORDER = ["Weight Loss", "Women's Hormones", "Men's Hormones", "Aesthetics"];
  const categories = [...new Set(plans.map((p) => p.category))].sort((a, b) => (ORDER.indexOf(a) + 1 || 99) - (ORDER.indexOf(b) + 1 || 99));

  return (
    <section id="memberships" data-testid="memberships-section" className="border-t border-linen bg-ivory py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-6 sm:px-8">
        <Reveal>
          <Kicker>Memberships</Kicker>
          <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <h2 className="max-w-xl font-serif text-4xl font-medium leading-tight tracking-tight text-forest sm:text-5xl">
              Consistent care, member pricing
            </h2>
            <p className="max-w-md text-sm leading-relaxed text-moss sm:text-base">
              Monthly plans for weight loss, hormone care and aesthetics. Request
              online, pay at the salon, and track everything in your patient portal.
            </p>
          </div>
        </Reveal>

        {categories.map((cat) => {
          const group = plans.filter((p) => p.category === cat);
          return (
            <div key={cat} data-testid={`membership-group-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="mt-14">
              <h3 className="font-serif text-2xl font-medium text-forest">{cat.includes("Hormones") ? cat : `${cat} Memberships`}</h3>
              <div className={`mt-6 grid gap-6 md:grid-cols-2 ${group.length >= 4 ? "xl:grid-cols-4" : group.length === 3 ? "lg:grid-cols-3" : ""}`}>
                {group.map((p, i) => (
                  <Reveal key={p.id} delay={i * 0.04}>
                    <article data-testid={`membership-plan-${p.id}`} className={`relative flex h-full flex-col rounded-lg border p-7 ${p.badge ? "border-forest" : "border-linen"}`}>
                      {p.badge && <span data-testid={`membership-badge-${p.id}`} className="absolute -top-3 left-6 rounded-md bg-forest px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-ivory">{p.badge}</span>}
                      <h4 className="font-serif text-2xl font-medium text-forest">{p.name}</h4>
                      <p className="mt-3 font-serif text-5xl font-medium text-forest">${p.price}<span className="ml-1 font-sans text-sm font-normal text-moss">/{p.interval}</span></p>
                      {p.description && <p className="mt-3 text-sm leading-relaxed text-moss">{p.description}</p>}
                      <ul className="mt-5 flex-1 space-y-2 text-sm text-moss">
                        {p.perks.map((perk) => (
                          <li key={perk} className="flex items-start gap-2.5"><Check size={15} className="mt-0.5 shrink-0 text-forest" />{perk}</li>
                        ))}
                      </ul>
                      {p.footnote && <p className="mt-4 text-xs italic text-moss">*{p.footnote}</p>}
                      <button data-testid={`membership-request-${p.id}`} onClick={() => setSelected(p)} className={`mt-6 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${p.badge ? "bg-forest text-ivory hover:bg-forest-dark" : "border border-linen text-forest hover:bg-forest hover:text-ivory"}`}>
                        Request Membership
                      </button>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          );
        })}

        <p className="mt-10 text-xs text-moss">
          Membership pricing cannot be combined with package discounts.{" "}
          {placeholder && <span className="italic">Aesthetics plan shown is a sample — final pricing coming soon.</span>}{" "}
          Already a member? <Link to="/portal" className="font-semibold text-forest hover:underline">Open your patient portal</Link>.
        </p>
      </div>
      {selected && <RequestModal plan={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}
